// Browser-safe inventory size helpers. The floor and admin UI bundle this file (ui/src/priceCalculator.ts,
// ui/src/admin.ts), so it must import nothing that needs Node or a database: only inventoryQuantity.ts.
// Split out of inventoryStockState.ts on 2026-10-09 after that file gained a node:crypto import (M5-R4-F1)
// and the UI build failed with 'Could not resolve "node:crypto"'.

import {
  divideScaledQuantities,
  formatQuantity,
  multiplyScaledQuantities,
  parseQuantity,
} from "./inventoryQuantity.ts";

/** INV8 Section 2.2. sizeUnit === "oz" is the identity path; "ml" and "l"
 * convert through the exact US fluid ounce, 29.5735295625 mL (the same
 * figure DEC-123's own keg-volume research already used). sizeScaled is
 * scaled at inventoryQuantity.ts's own SCALE (10,000, four decimal
 * places); the ounce constant below is exact to ten decimal places
 * (295735295625 at a 10^10 scale), so sizeScaled is first multiplied by
 * 10^6 (10^10 / SCALE) before dividing, which lets divideScaledQuantities'
 * own "* SCALE" internal step land the division at the constant's real
 * 10^10 precision instead of first truncating it down to SCALE's four
 * decimal places. Returns null for any sizeUnit this codebase's own
 * SIZE_UNIT_DISPLAY map (ui/src/admin.ts) never writes, a defensive branch
 * rather than a real data path. */
const US_FLUID_OUNCE_IN_ML_TIMES_1E10 = 295735295625n;

export function convertSizeToOunces(sizeValue: string, sizeUnit: string): string | null {
  if (sizeUnit !== "oz" && sizeUnit !== "ml" && sizeUnit !== "l") {
    return null;
  }
  const sizeScaled = parseQuantity(sizeValue, "sizeValue");
  if (sizeUnit === "oz") {
    return formatQuantity(sizeScaled);
  }
  const mlScaled = sizeUnit === "l" ? sizeScaled * 1000n : sizeScaled;
  const ouncesScaled = divideScaledQuantities(
    mlScaled * 1_000_000n,
    US_FLUID_OUNCE_IN_ML_TIMES_1E10,
  );
  return formatQuantity(ouncesScaled);
}

/**
 * Ounces in one whole container for a product, or null when the product has
 * no usable container configuration (never a guess). Lifted out of
 * prepared.ts (INV21) so the Prepared posting and this cost read use ONE
 * rule and cannot disagree about which source applies.
 */
export function resolveOuncesPerContainer(
  product: {
    container_type: string | null;
    size_value: string | null;
    size_unit: string | null;
    default_keg_size: string | null;
  },
  kegSizeVolumeOzByValue: ReadonlyMap<string, string>,
): string | null {
  if (product.container_type === "keg") {
    return product.default_keg_size !== null
      ? (kegSizeVolumeOzByValue.get(product.default_keg_size) ?? null)
      : null;
  }
  return product.container_type !== null &&
    product.size_value !== null &&
    product.size_unit !== null
    ? convertSizeToOunces(product.size_value, product.size_unit)
    : null;
}


/** KEGMATH1: the price paths' form of resolveOuncesPerContainer. Pricing has always worked from Size for an item whose
 * container type is not set (a bare bottle, an item kind's default), so a null container type is read as "not a keg"
 * rather than "no container"; a keg is still its keg size's volume, never Size. */
export function resolveOuncesPerContainerForPrice(
  product: {
    container_type: string | null;
    size_value: string | null;
    size_unit: string | null;
    default_keg_size: string | null;
  },
  kegSizeVolumeOzByValue: ReadonlyMap<string, string>,
): string | null {
  return resolveOuncesPerContainer(
    { ...product, container_type: product.container_type ?? "other" },
    kegSizeVolumeOzByValue,
  );
}

// ADDITEM2 (DEC-611): keg foam and waste. ONE place turns a keg's waste into the three numbers everything else needs, so
// depletion, expected pours, cost per usable ounce and the suggested price cannot disagree. The ledger stays in the keg's
// real ounces (on hand in kegs, par and reorder proposals read it unchanged); a pour takes pour / (1 - waste) ounces out of
// it; usable ounces per keg are volume x (1 - waste). Basis points: 1000 is 10%. Waste applies to kegs only.
export const FULL_BASIS_POINTS = 10_000;

/** The waste a keg item uses: its own, or (null) the tenant's keg usable yield turned around (yield 9000 is waste 1000). */
export function resolveKegWasteBasisPoints(
  itemWasteBasisPoints: number | null,
  tenantUsableYieldBasisPoints: number,
): number {
  return itemWasteBasisPoints ?? FULL_BASIS_POINTS - tenantUsableYieldBasisPoints;
}

/** Ounces of a container that can be poured: for a keg its volume x (1 - waste); any other container is whole. Null when
 * resolveOuncesPerContainer has no volume (never a guess). */
export function resolveUsableOuncesPerContainer(
  product: {
    container_type: string | null;
    size_value: string | null;
    size_unit: string | null;
    default_keg_size: string | null;
  },
  kegSizeVolumeOzByValue: ReadonlyMap<string, string>,
  wasteBasisPoints: number,
): string | null {
  const gross = resolveOuncesPerContainer(product, kegSizeVolumeOzByValue);
  if (gross === null || product.container_type !== "keg") return gross;
  return formatQuantity(
    multiplyScaledQuantities(parseQuantity(gross), BigInt(FULL_BASIS_POINTS - wasteBasisPoints)),
  );
}

/** resolveUsableOuncesPerContainer in its price form (a null container type is read as "not a keg", as in
 * resolveOuncesPerContainerForPrice): the ounces a price is divided over. */
export function resolveUsableOuncesPerContainerForPrice(
  product: {
    container_type: string | null;
    size_value: string | null;
    size_unit: string | null;
    default_keg_size: string | null;
  },
  kegSizeVolumeOzByValue: ReadonlyMap<string, string>,
  wasteBasisPoints: number,
): string | null {
  return resolveUsableOuncesPerContainer(
    { ...product, container_type: product.container_type ?? "other" },
    kegSizeVolumeOzByValue,
    wasteBasisPoints,
  );
}

/** Ounces a pour takes out of the container in the ledger: the pour itself, plus its foam for a keg. Unchanged for any
 * other container, and unchanged at 0% waste. Returned as a number at the ledger's four decimals. */
export function depletionOuncesForPour(
  pourOunces: number,
  containerType: string | null,
  wasteBasisPoints: number,
): number {
  if (containerType !== "keg" || wasteBasisPoints === 0) return pourOunces;
  const pourScaled = parseQuantity(pourOunces.toFixed(4), "pour ounces");
  const depleted = divideScaledQuantities(pourScaled, BigInt(FULL_BASIS_POINTS - wasteBasisPoints));
  return Number(formatQuantity(depleted));
}

// UX16-F2 (docs/build-briefs/UX16-F2-negative-on-hand-shown-not-hidden.md;
// DEC-178). True when a quantity string parses to a value below zero. Used
// only to decide whether a DISPLAY warning is shown; never changes what is
// stored, what quantityStateOf classifies it as, or whether a sale is
// allowed.
export function isNegativeQuantity(quantity: string): boolean {
  return parseQuantity(quantity) < 0n;
}
